import type { UserClaims } from './types';
import { SpaceRoles, UserFeatures, UserRoles } from './types';

// Helper function to check if a space has a specific role
function hasRoleInSpace(space: string, claims: UserClaims, role: SpaceRoles): boolean {
  if (role === SpaceRoles.Editor) {
    return (
      claims.coaches?.[space]?.role === role
      // special case for editors: If we have claims for the space but no role
      // is defined, we assume the user is an editor
      || (claims.coaches?.[space] !== undefined
        && claims.coaches?.[space]?.role === undefined)
    );
  }
  return claims.coaches?.[space]?.role === role;
}

export async function getActiveSpaceServer(): Promise<string> {
  const { headers } = await import('next/headers');
  const headersList = await headers();
  const headerSpace = headersList.get('x-dashboard-space-id');
  if (headerSpace !== null && headerSpace !== '') {
    return headerSpace;
  }

  return '';
}

/**
 * @deprecated Space is now read from URL path only. Use useSpaceId() from '@/stores/space' instead.
 */
export function getActiveSpaceClient(): string {
  return '';
}

// testing
// Map user roles and space roles to features
const roleFeaturesMap: Record<UserRoles | SpaceRoles, UserFeatures[]> = {
  [UserRoles.SuperAdmin]: [
    UserFeatures.ABTestFeature,
    UserFeatures.ContentEditing,
    UserFeatures.CertificateReview,
    UserFeatures.ContentReview,
    UserFeatures.SpaceManagement,
  ],
  [UserRoles.Admin]: [
    UserFeatures.ContentEditing,
    UserFeatures.CertificateReview,
    UserFeatures.ContentReview,
    UserFeatures.SpaceManagement,
  ],
  [SpaceRoles.Editor]: [
    UserFeatures.ContentEditing,
    UserFeatures.CertificateReview,
  ],
  [SpaceRoles.SpaceOwner]: [
    UserFeatures.SpaceManagement,
    UserFeatures.CertificateReview,
    UserFeatures.ContentReview,
  ],
  [SpaceRoles.CertificateReviewer]: [UserFeatures.CertificateReview],
  [SpaceRoles.ContentReviewer]: [UserFeatures.ContentReview],
  [SpaceRoles.ContentManager]: [UserFeatures.ContentEditing],
};

/**
 * Check if a role is an admin role (SuperAdmin or Admin)
 * @param role - The role to check
 * @returns True if the role is SuperAdmin or Admin
 */
export function isAdminRole(role: UserRoles | SpaceRoles | string | null | undefined): boolean {
  return role === UserRoles.SuperAdmin || role === UserRoles.Admin;
}

// Determine user role based on claims and active space
export function getUserRoleByClaims(claims: UserClaims, space: string): UserRoles | SpaceRoles | null {
  const { role: globalRole } = claims;

  // Return global roles if available
  if (globalRole !== null && globalRole !== undefined && isAdminRole(globalRole) === true) {
    return globalRole;
  }

  // Check space-specific roles
  if (hasRoleInSpace(space, claims, SpaceRoles.CertificateReviewer)) {
    return SpaceRoles.CertificateReviewer;
  }
  if (hasRoleInSpace(space, claims, SpaceRoles.ContentReviewer)) {
    return SpaceRoles.ContentReviewer;
  }
  if (hasRoleInSpace(space, claims, SpaceRoles.SpaceOwner)) {
    return SpaceRoles.SpaceOwner;
  }
  if (hasRoleInSpace(space, claims, SpaceRoles.ContentManager)) {
    return SpaceRoles.ContentManager;
  }
  if (hasRoleInSpace(space, claims, SpaceRoles.Editor)) {
    return SpaceRoles.Editor;
  }

  return null;
}

// Get the user's features based on their role
export function getUserFeatures(claims: UserClaims, space: string): UserFeatures[] {
  const role = getUserRoleByClaims(claims, space);
  return role != null ? roleFeaturesMap[role] || [] : [];
}

// Check if the user has access to the required features
export function getHasAccess(requiredFeatures: UserFeatures[], userFeatures: UserFeatures[]): boolean {
  return requiredFeatures.some(feature => userFeatures.includes(feature));
}
